import { cn } from "@/lib/utils";
import { format } from "date-fns";
import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectItem,
  SelectValue,
  SelectContent,
  SelectTrigger,
} from "@/components/ui/select";
import {
  X,
  Star,
  Edit,
  Check,
  Upload,
  QrCode,
  Download,
  Clipboard,
  CreditCard,
  CheckCircle,
  CalendarIcon,
  CheckCircle2,
} from "lucide-react";

interface ElementShowcaseProps {
  className?: string;
}

export function ElementShowcase({ className }: ElementShowcaseProps) {
  const [selectedElement, setSelectedElement] = useState<string | null>(
    "qrcode"
  );
  const [date, setDate] = useState<Date>();
  const [rating, setRating] = useState<number>(0);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [signature, setSignature] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [qrUrl, setQrUrl] = useState("https://www.formcraft.cloud/pricing");
  const [copied, setCopied] = useState(false);
  const [selectedNPS, setSelectedNPS] = useState<number | null>(null);

  const elements = [
    { id: "text", label: "Text" },
    { id: "textarea", label: "Textarea" },
    { id: "checkbox", label: "Checkbox" },
    { id: "radio", label: "Radio" },
    { id: "select", label: "Select" },
    { id: "slider", label: "Slider" },
    { id: "rating", label: "Rating" },
    { id: "date", label: "Date" },
    { id: "file", label: "File Upload" },
    { id: "signature", label: "Signature" },
    { id: "nps", label: "NPS" },
    { id: "address", label: "Address" },
    { id: "qrcode", label: "QR Code" },
    // { id: "stripe", label: "Payment" },
    { id: "multiselect", label: "Multi-Select" },
    { id: "table", label: "Table" },
    { id: "divider", label: "Divider" },
    { id: "pagebreak", label: "Page Break" },
    { id: "heading", label: "Heading" },
    { id: "paragraph", label: "Paragraph" },
    { id: "richtext", label: "Rich Text" },
    { id: "time", label: "Time" },
    { id: "number", label: "Number" },
    { id: "email", label: "Email" },
    { id: "phone", label: "Phone" },
  ];

  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(" ");
    } else {
      return value;
    }
  };

  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");

    if (v.length >= 3) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }

    return v;
  };

  // Handle payment submission
  const handlePayment = () => {
    if (!cardNumber || !expiry || !cvc) return;

    setProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      setProcessing(false);
      setPaymentComplete(true);
    }, 1000);
  };

  // Reset payment
  const resetPayment = () => {
    setCardNumber("");
    setExpiry("");
    setCvc("");
    setPaymentComplete(false);
  };

  // Get NPS category
  const getNPSCategory = () => {
    if (selectedNPS === null) return null;
    if (selectedNPS <= 6) return { text: "Detractor", color: "text-red-500" };
    if (selectedNPS <= 8) return { text: "Passive", color: "text-yellow-500" };
    return { text: "Promoter", color: "text-green-500" };
  };

  const npsCategory = getNPSCategory();

  // Copy QR URL
  const handleCopy = () => {
    navigator.clipboard.writeText(qrUrl);
    setCopied(true);

    // Reset copied state after 2 seconds
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <div className={cn("w-full", className)}>
      <div className="rounded-t-lg bg-muted p-3 flex flex-wrap gap-2 overflow-x-auto">
        {elements.map((element) => (
          <Badge
            key={element.id}
            variant={selectedElement === element.id ? "default" : "outline"}
            className="cursor-pointer hover:bg-primary/20"
            onClick={() => setSelectedElement(element.id)}
          >
            {element.label}
          </Badge>
        ))}
      </div>

      <Card className="rounded-t-none shadow-md border">
        <CardContent className="p-6">
          {selectedElement === "text" && (
            <div className="space-y-2">
              <Label htmlFor="text-demo">What's your name?</Label>
              <Input id="text-demo" placeholder="Enter your name" />
              <p className="text-xs text-muted-foreground">
                This is a simple text input field
              </p>
            </div>
          )}

          {selectedElement === "email" && (
            <div className="space-y-2">
              <Label htmlFor="email-demo">Email Address</Label>
              <Input
                id="email-demo"
                type="email"
                placeholder="your@email.com"
              />
              <p className="text-xs text-muted-foreground">
                Collects email addresses with validation
              </p>
            </div>
          )}

          {selectedElement === "number" && (
            <div className="space-y-2">
              <Label htmlFor="number-demo">Age</Label>
              <Input
                id="number-demo"
                type="number"
                placeholder="Enter your age"
                min={0}
                max={120}
              />
              <p className="text-xs text-muted-foreground">
                Numeric input with min/max validation
              </p>
            </div>
          )}

          {selectedElement === "phone" && (
            <div className="space-y-2">
              <Label htmlFor="phone-demo">Phone Number</Label>
              <Input id="phone-demo" placeholder="(123) 456-7890" />
              <p className="text-xs text-muted-foreground">
                Collects formatted phone numbers
              </p>
            </div>
          )}

          {selectedElement === "textarea" && (
            <div className="space-y-2">
              <Label htmlFor="textarea-demo">Tell us about yourself</Label>
              <Textarea
                id="textarea-demo"
                placeholder="Write your response here..."
              />
              <p className="text-xs text-muted-foreground">
                For longer form responses
              </p>
            </div>
          )}

          {selectedElement === "richtext" && (
            <div className="space-y-2">
              <Label htmlFor="richtext-demo">Article Content</Label>
              <div className="border rounded-md p-2">
                <div className="border-b pb-2 mb-2 flex gap-2">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                    B
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0 italic"
                  >
                    I
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0 underline"
                  >
                    U
                  </Button>
                  <div className="w-px h-8 bg-border mx-1"></div>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                    H1
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                    H2
                  </Button>
                </div>
                <div className="min-h-24 p-2">
                  <p>
                    This is <strong>rich</strong> <em>text</em> content with{" "}
                    <u>formatting</u>.
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Rich text editor with formatting options
              </p>
            </div>
          )}

          {selectedElement === "checkbox" && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="terms" />
                <Label htmlFor="terms">
                  I agree to the terms and conditions
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="marketing" defaultChecked />
                <Label htmlFor="marketing">Subscribe to our newsletter</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Allow users to select multiple options
              </p>
            </div>
          )}

          {selectedElement === "radio" && (
            <div className="space-y-2">
              <Label>What's your preferred contact method?</Label>
              <RadioGroup defaultValue="email">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="email" id="radio-email" />
                  <Label htmlFor="radio-email">Email</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="phone" id="radio-phone" />
                  <Label htmlFor="radio-phone">Phone</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="text" id="radio-text" />
                  <Label htmlFor="radio-text">Text Message</Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground">
                For selecting one option from many
              </p>
            </div>
          )}

          {selectedElement === "select" && (
            <div className="space-y-2">
              <Label htmlFor="select-demo">Select your role</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="developer">Developer</SelectItem>
                  <SelectItem value="designer">Designer</SelectItem>
                  <SelectItem value="product">Product Manager</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Dropdown selector for choosing one option
              </p>
            </div>
          )}

          {selectedElement === "multiselect" && (
            <div className="space-y-2">
              <Label htmlFor="multiselect-demo">Skills</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select skills" />
                </SelectTrigger>
                <SelectContent>
                  <div className="flex items-center space-x-2 p-1">
                    <Checkbox id="js" />
                    <Label htmlFor="js">JavaScript</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-1">
                    <Checkbox id="react" checked />
                    <Label htmlFor="react">React</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-1">
                    <Checkbox id="node" />
                    <Label htmlFor="node">Node.js</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-1">
                    <Checkbox id="ts" checked />
                    <Label htmlFor="ts">TypeScript</Label>
                  </div>
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1 mt-2">
                <Badge>React</Badge>
                <Badge>TypeScript</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Select multiple options from a dropdown
              </p>
            </div>
          )}

          {selectedElement === "slider" && (
            <div className="space-y-4">
              <Label>How satisfied are you with our service? (1-10)</Label>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">1</span>
                  <span className="font-medium text-primary">7</span>
                  <span className="text-sm text-muted-foreground">10</span>
                </div>

                <Slider defaultValue={[7]} max={10} step={1} className="py-2" />

                <div className="flex justify-between mt-1">
                  {[1, 3.25, 5.5, 7.75, 10].map((markValue, index) => (
                    <div key={index} className="flex flex-col items-center">
                      <div className="w-0.5 h-1.5 bg-muted-foreground/30"></div>
                      <span className="text-[10px] text-muted-foreground mt-0.5">
                        {Math.round(markValue)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Perfect for rating scales and numeric inputs
              </p>
            </div>
          )}

          {selectedElement === "rating" && (
            <div className="space-y-2">
              <Label>Rate your experience</Label>
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className={cn(
                      "h-8 w-8 cursor-pointer",
                      i <= rating
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted"
                    )}
                    onClick={() => setRating(i)}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Star ratings for feedback and reviews
              </p>
            </div>
          )}

          {selectedElement === "date" && (
            <div className="space-y-2">
              <Label>Select a date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Calendar-based date selection
              </p>
            </div>
          )}

          {selectedElement === "time" && (
            <div className="space-y-2">
              <Label>Select a time</Label>
              <div className="grid grid-cols-3 gap-2">
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Hour" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Minute" />
                  </SelectTrigger>
                  <SelectContent>
                    {["00", "15", "30", "45"].map((minute) => (
                      <SelectItem key={minute} value={minute}>
                        {minute}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="AM/PM" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="am">AM</SelectItem>
                    <SelectItem value="pm">PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Time selection inputs
              </p>
            </div>
          )}

          {selectedElement === "file" && (
            <div className="space-y-2">
              <Label>Upload a document</Label>
              {!fileUploaded ? (
                <div
                  className="border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => setFileUploaded(true)}
                >
                  <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground text-sm">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PDF, DOCX, JPG, PNG (max 5MB)
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between border rounded-md p-3">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <span>document.pdf</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFileUploaded(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                For uploading files and documents
              </p>
            </div>
          )}

          {selectedElement === "signature" && (
            <div className="space-y-2">
              <Label>Signature</Label>
              <div
                className="relative border rounded-md p-2 bg-white h-[140px] cursor-crosshair"
                onClick={() => setSignature(!signature)}
              >
                {!signature ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center text-muted-foreground">
                      <Edit className="h-4 w-4 mr-2" />
                      <span>Sign here</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="h-full w-full flex items-center justify-center">
                      <span className="font-signature text-3xl">John Doe</span>
                    </div>
                    <div className="absolute top-2 right-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSignature(false);
                        }}
                        type="button"
                        className="h-7 px-2 text-xs"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Clear
                      </Button>
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Add electronic signatures to documents
              </p>
            </div>
          )}

          {selectedElement === "table" && (
            <div className="space-y-2">
              <Label>Product Inventory</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium">
                        Product
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium">
                        Quantity
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium">
                        Price
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="px-4 py-2">
                        <Input placeholder="Product name" className="h-8" />
                      </td>
                      <td className="px-4 py-2">
                        <Input type="number" placeholder="0" className="h-8" />
                      </td>
                      <td className="px-4 py-2">
                        <Input placeholder="$0.00" className="h-8" />
                      </td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-4 py-2">
                        <Input placeholder="Product name" className="h-8" />
                      </td>
                      <td className="px-4 py-2">
                        <Input type="number" placeholder="0" className="h-8" />
                      </td>
                      <td className="px-4 py-2">
                        <Input placeholder="$0.00" className="h-8" />
                      </td>
                    </tr>
                    <tr className="border-t">
                      <td colSpan={3} className="px-4 py-2 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-7"
                        >
                          Add Row
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Multi-row tabular data entry
              </p>
            </div>
          )}

          {selectedElement === "divider" && (
            <div className="space-y-2">
              <Label>Form Section Divider</Label>
              <div className="py-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Section 2: Additional Information
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Visually separate form sections
              </p>
            </div>
          )}

          {selectedElement === "heading" && (
            <div className="space-y-2">
              <Label>Form Heading</Label>
              <div className="py-2">
                <h2 className="text-2xl font-bold">Contact Information</h2>
              </div>
              <p className="text-xs text-muted-foreground">
                Add headings to organize form sections
              </p>
            </div>
          )}

          {selectedElement === "paragraph" && (
            <div className="space-y-2">
              <Label>Form Instructions</Label>
              <div className="py-2">
                <p className="text-muted-foreground">
                  Please provide your contact information below. All fields
                  marked with an asterisk (*) are required.
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Add explanatory text to your forms
              </p>
            </div>
          )}

          {selectedElement === "pagebreak" && (
            <div className="space-y-2">
              <Label>Page Break</Label>
              <div className="py-6 px-4 border rounded-lg bg-muted/20 flex flex-col items-center justify-center">
                <div className="w-full border-t-2 border-dashed border-primary/40 my-2"></div>
                <div className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded mt-1">
                  Page Break: Next Page
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  This divides your form into multiple pages
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Split long forms into multiple pages
              </p>
            </div>
          )}

          {selectedElement === "nps" && (
            <div className="space-y-2">
              <Label>How likely are you to recommend our product?</Label>
              <div className="grid grid-cols-11 gap-1 mt-2">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <Button
                    key={num}
                    type="button"
                    variant={selectedNPS === num ? "default" : "outline"}
                    className="h-10 w-10 p-0 flex items-center justify-center hover:bg-primary hover:text-primary-foreground"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent form submission
                      setSelectedNPS(num);
                    }}
                  >
                    {num}
                  </Button>
                ))}
              </div>

              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Not at all likely</span>
                <span>Extremely likely</span>
              </div>

              {npsCategory && (
                <div
                  className={`mt-2 text-center font-medium ${npsCategory.color}`}
                >
                  {npsCategory.text}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Net Promoter Score for feedback
              </p>
            </div>
          )}

          {selectedElement === "address" && (
            <div className="space-y-3">
              <Label>Delivery Address</Label>
              <div className="grid gap-2">
                <Input placeholder="Street Address" />
                <Input placeholder="Apartment, suite, etc. (optional)" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="City" />
                  <Input placeholder="State/Province" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="ZIP/Postal Code" />
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us">United States</SelectItem>
                      <SelectItem value="ca">Canada</SelectItem>
                      <SelectItem value="uk">United Kingdom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Collect full address information
              </p>
            </div>
          )}

          {selectedElement === "qrcode" && (
            <div className="space-y-2">
              <Label>QR Code Generator</Label>
              <div className="flex flex-col sm:flex-row gap-4 items-center mt-2">
                {/* QR Code Preview */}
                <div className="flex-shrink-0 bg-white p-4 border rounded-md">
                  <div className="w-[120px] h-[120px] flex items-center justify-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                        qrUrl
                      )}`}
                      alt="QR Code"
                      width={120}
                      height={120}
                      className="mx-auto"
                    />
                  </div>
                </div>

                <div className="w-full space-y-3">
                  {/* URL Input */}
                  <div className="space-y-1">
                    <Label htmlFor="qr-url" className="text-sm">
                      URL for QR Code
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="qr-url"
                        value={qrUrl}
                        onChange={(e) => setQrUrl(e.target.value)}
                        placeholder="Enter URL for QR code"
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopy}
                        className={
                          copied ? "border-green-500 text-green-500" : ""
                        }
                      >
                        {copied ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Clipboard className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="flex justify-between items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download QR Code
                    </Button>

                    <div className="text-xs text-muted-foreground">
                      Scan with any QR code reader
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Generate QR codes for websites, contacts, and more
              </p>
            </div>
          )}

          {/* {selectedElement === "stripe" && (
            <div className="space-y-2">
              <Label>Payment</Label>
              <div className="border rounded-md p-4 mt-2">
                {paymentComplete ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <h3 className="font-medium text-green-700">
                        Payment Successful
                      </h3>
                    </div>

                    <div className="bg-muted/20 p-3 rounded-md">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Amount:</span>
                        <span className="font-medium">$19.99</span>
                      </div>

                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-muted-foreground">Card:</span>
                        <span className="font-medium">
                          •••• {cardNumber.slice(-4) || "4242"}
                        </span>
                      </div>

                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-muted-foreground">Date:</span>
                        <span className="font-medium">
                          {new Date().toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={resetPayment}
                    >
                      Reset payment
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <CreditCard className="h-6 w-6 text-primary" />
                      <div>
                        <h3 className="text-sm font-medium">Secure Payment</h3>
                        <p className="text-xs text-muted-foreground">
                          Your payment information is encrypted
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mb-4">
                      <span className="text-sm font-medium">Amount:</span>
                      <span className="text-lg font-semibold">$19.99</span>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="card-number" className="text-xs">
                          Card Number
                        </Label>
                        <Input
                          id="card-number"
                          placeholder="1234 5678 9012 3456"
                          value={cardNumber}
                          onChange={(e) =>
                            setCardNumber(formatCardNumber(e.target.value))
                          }
                          maxLength={19}
                          className="mt-1"
                        />
                      </div>

                      <div className="flex gap-3">
                        <div className="flex-1">
                          <Label htmlFor="expiry" className="text-xs">
                            Expiry
                          </Label>
                          <Input
                            id="expiry"
                            placeholder="MM/YY"
                            value={expiry}
                            onChange={(e) =>
                              setExpiry(formatExpiry(e.target.value))
                            }
                            maxLength={5}
                            className="mt-1"
                          />
                        </div>

                        <div className="flex-1">
                          <Label htmlFor="cvc" className="text-xs">
                            CVC
                          </Label>
                          <Input
                            id="cvc"
                            placeholder="123"
                            value={cvc}
                            onChange={(e) =>
                              setCvc(e.target.value.replace(/[^\d]/g, ""))
                            }
                            maxLength={3}
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <Button
                        className="w-full mt-2"
                        onClick={handlePayment}
                        disabled={
                          cardNumber.length < 16 ||
                          expiry.length < 5 ||
                          cvc.length < 3 ||
                          processing
                        }
                      >
                        {processing ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Processing...
                          </span>
                        ) : (
                          <span>Pay $19.99</span>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Securely collect credit card payments
              </p>
            </div>
          )} */}
        </CardContent>
      </Card>
    </div>
  );
}
